// Portal module catalog (frontend mirror of server/src/modules.ts).
// `key` = permission identifier, `icon` = tabler icon (without "ti-").
// Display names are localized via i18n key `mod_<key>`.
export interface ModuleMeta { key: string; icon: string; url?: string; } // url → opens an external app

export const MODULES: ModuleMeta[] = [
  // Front five (Calem 2026-09-21): Peak, SLF Property, Email, Doc Storage, Panda Marketing lead the
  // main menu grid in this exact order; everything else keeps its prior relative order below them.
  { key: 'peak', icon: 'files' }, // Peak: Quotation / Invoice / Receipt (invoicing)
  // SLF Property (container): Due Diligence + Property Transfer. Same derived-visibility rule as Panda Marketing.
  { key: 'slf_property', icon: 'building-estate' },
  // The SLF service-line row (owner 2026-09-22): License, Visa and Company sit right after
  // Property so the SLF™ family reads as one block in the menu. Each is a desk console since
  // 2026-09-23 (components/ServiceDesk on the Workflows engine, routed in App.tsx).
  { key: 'slf_license', icon: 'license' },
  { key: 'slf_visa', icon: 'plane-departure' },
  { key: 'slf_company', icon: 'building' },
  { key: 'tax', icon: 'receipt-tax' },
  { key: 'ai_counsel', icon: 'gavel' },   // law library + AI legal-opinion drafting (lawyers)
  { key: 'email', icon: 'mail' },
  { key: 'doc_storage', icon: 'archive' }, // Document Storage — the firm's by-type document archive (Calem 2026-08-15)
  // Panda Marketing (container): Leads + AI Marketing + Social Poster + AI Working Reports. Not a real
  // permission — shown when the user holds ANY of those sub-modules; each tab is gated individually.
  { key: 'panda_marketing', icon: 'speakerphone' },
  { key: 'social_poster', icon: 'speakerphone', url: 'https://slf.pandabot.ai' },
  { key: 'pettycash', icon: 'coins' },
  { key: 'bank_acc', icon: 'building-bank' },
  { key: 'bank_legal', icon: 'scale' },
  { key: 'bank_client', icon: 'users' },   // SCB Dedicated Client Account — funds held FOR clients
  { key: 'quotation', icon: 'file-dollar' },
  { key: 'active_jobs', icon: 'briefcase' },
  { key: 'closed_jobs', icon: 'folder-check' },
  // Accounting fee board — every accounting client × every month, paid / late / outstanding.
  // Its own key because it shows the firm's whole receivable position (owner 2026-09-18).
  { key: 'taken_return', icon: 'arrows-exchange' },
  { key: 'bookkeeping', icon: 'book' },
  // SLF™ Accounting — the statutory books. Its own tile because it is a different job from the
  // invoice line-item work in 'bookkeeping', done by different people, and it is where an accountant
  // spends the day rather than somewhere to reach through the invoicing app.
  { key: 'accounting_books', icon: 'book-2' },
  { key: 'compliance', icon: 'calendar-due' },
  { key: 'leads', icon: 'target-arrow' },
  { key: 'ai_marketing', icon: 'map-search' }, // AI explores Koh Samui businesses → editable table
  { key: 'contact', icon: 'address-book' },
  { key: 'invoice', icon: 'file-invoice' },
  { key: 'receipt', icon: 'receipt' },
  { key: 'fee_board', icon: 'table-options' },
  { key: 'due_diligence', icon: 'file-search' },
  { key: 'property_transfer', icon: 'home-move' },
  { key: 'poa', icon: 'writing-sign' },   // Power of Attorney — 1 form → N grouped POAs
  { key: 'lease_reg', icon: 'file-certificate' },
  { key: 'workflows', icon: 'route' },   // Service Workflows — per-service client-facing step flows   // Lease Registration — READ-ONLY mirror of slf-legal.co's fee assessments
  { key: 'services', icon: 'packages' },
  { key: 'staff_dashboard', icon: 'layout-dashboard' }, // each employee's own dashboard
  { key: 'finance_report', icon: 'report-money' },
  { key: 'calendar', icon: 'calendar-month' }, // team calendars (CalDAV) on the dashboard
  // Client-portal logins (slf-accounting.co/portal) — a normal permission since 2026-08-29,
  // so it can be handed to an employee instead of living behind the admin panel.
  { key: 'portal_accounts', icon: 'id-badge' },
  { key: 'bugify', icon: 'bug' },   // Bugify — everyone can file a bug or a suggestion
];

export const MODULE_KEYS = MODULES.map(m => m.key);
export const isValidModule = (k: string) => MODULE_KEYS.includes(k);
export const moduleIcon = (k: string) => MODULES.find(m => m.key === k)?.icon || 'box';
export const moduleUrl = (k: string) => MODULES.find(m => m.key === k)?.url;

// Modules everyone can access regardless of assignment (mirror of backend).
export const SHARED_MODULES = ['contact', 'staff_dashboard', 'bugify'];
